import { z } from 'zod'

/**
 * Validação do módulo de autenticação.
 *
 * Substitui os sete `validationSchema.js` em Yup. O `yup-locale-pt` fornecia as
 * mensagens padrão em português; sem equivalente configurado no projeto, cada
 * mensagem passa a ser explícita — o que também corrigiu as que chegavam ao
 * participante sem acento ("Digite no minimo 8 caracteres", "Senha nao pode
 * conter espacos", "Confirmacao de senha", "Senhas devem estar iguais").
 *
 * Sobre `nullable()`: o Yup aceitava `null` e tratava string vazia como ausente
 * por conta do `required()`. Aqui o piso é `min(1)`, que rejeita string vazia
 * diretamente, e `preprocess` normaliza `null`/`undefined` para string vazia —
 * os campos nascem com `''` no formulário, mas o valor gravado em cookie pode
 * voltar como `null`.
 */

const texto = (mensagemObrigatorio: string) =>
  z.preprocess(
    valor => (valor == null ? '' : valor),
    z.string().min(1, mensagemObrigatorio)
  )

const email = (mensagemObrigatorio = 'Informe seu e-mail') =>
  z.preprocess(
    valor => (valor == null ? '' : valor),
    z
      .string()
      .min(1, mensagemObrigatorio)
      .email('Informe um e-mail válido')
  )

const senha = (rotulo: string) =>
  z.preprocess(
    valor => (valor == null ? '' : valor),
    z
      .string()
      .min(1, `Informe ${rotulo}`)
      .min(8, 'Digite no mínimo 8 caracteres')
      .max(128, 'Digite no máximo 128 caracteres')
      .regex(/^\S*$/, 'A senha não pode conter espaços')
  )

export const signInSchema = z.object({
  login: texto('Informe seu login'),
  password: texto('Informe sua senha')
})

export const forgotPasswordSchema = z.object({
  email: email()
})

export const resendConfirmationSchema = z.object({
  email: email()
})

/**
 * `oneOf([ref])` do Yup vira `refine` com `path`, que é o que leva a mensagem
 * ao campo de confirmação em vez de ao formulário inteiro.
 */
export const resetPasswordSchema = z
  .object({
    password: senha('a nova senha'),
    password_confirmation: texto('Confirme a nova senha')
  })
  .refine(dados => dados.password === dados.password_confirmation, {
    message: 'As senhas devem ser iguais',
    path: ['password_confirmation']
  })

type SignUpOptions = {
  withTerms?: boolean
  withEmailConfirmation?: boolean
}

/**
 * O cadastro é condicional — a assinatura preserva a do schema anterior, que os
 * consumidores já chamam com estas opções.
 */
export const getSignUpSchema = ({
  withTerms = false,
  withEmailConfirmation = false
}: SignUpOptions = {}) => {
  const base = z.object({
    name: texto('Informe seu nome completo'),
    email: email(),
    password: senha('sua senha'),
    password_confirmation: texto('Confirme sua senha'),
    ...(withEmailConfirmation
      ? { email_confirmation: email('Confirme seu e-mail') }
      : {}),
    ...(withTerms
      ? {
          accept_terms: z.boolean().refine(aceito => aceito === true, {
            message: 'Aceite os termos para continuar'
          })
        }
      : {})
  })

  return base
    .refine(dados => dados.password === dados.password_confirmation, {
      message: 'As senhas devem ser iguais',
      path: ['password_confirmation']
    })
    .refine(
      dados =>
        !withEmailConfirmation ||
        (dados as { email_confirmation?: string }).email_confirmation ===
          dados.email,
      {
        message: 'Os e-mails devem ser iguais',
        path: ['email_confirmation']
      }
    )
}

// Inferência local, não em `types.ts`: aquele arquivo importa deste, e importar
// de volta criaria um ciclo — type-only, mas ainda um ciclo. É a mesma solução
// já adotada e documentada em `features/networking/model/schemas.ts`.
export type SignInValues = z.infer<typeof signInSchema>
export type ForgotPasswordValues = z.infer<typeof forgotPasswordSchema>
export type ResendConfirmationValues = z.infer<typeof resendConfirmationSchema>
export type ResetPasswordValues = z.infer<typeof resetPasswordSchema>
export type SignUpValues = z.infer<ReturnType<typeof getSignUpSchema>>
